import React, { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import toast from "react-hot-toast";
import { db } from "../firebase/config";
import Modal from "./Modal";

const INITIAL = {
  title: "",
  uploadType: "youtube",
  videoUrl: "",
  order: "",
  description: "",
  isPublished: "success",
};

function toDescriptionHtml(text) {
  const t = String(text ?? "").trim();
  if (!t) return "<p></p>";
  const escaped = t
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  return `<p>${escaped.replace(/\n/g, "<br />")}</p>`;
}

function looksLikeYouTubeUrl(url) {
  const s = String(url ?? "").trim().toLowerCase();
  if (!s) return false;
  return (
    s.includes("youtube.com/") ||
    s.includes("youtu.be/") ||
    s.includes("youtube.com/watch")
  );
}

function descriptionHtmlToPlain(html) {
  if (html == null || html === "") return "";
  return String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*/gi, "\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .trim();
}

function normalizeIsPublished(v) {
  if (v === true || v === "success") return "success";
  return "draft";
}

// function videoRecordToForm(editVideo) {
//   return {
//     title: String(editVideo.title ?? ""),
//     videoUrl: String(editVideo.videoUrl ?? ""),
//     order:
//       editVideo.order === undefined || editVideo.order === null
//         ? ""
//         : String(editVideo.order),
//     description: descriptionHtmlToPlain(editVideo.description),
//     isPublished: normalizeIsPublished(editVideo.isPublished),
//   };
// }

function videoRecordToForm(editVideo) {
  return {
    title: String(editVideo.title ?? ""),
    uploadType: editVideo.uploadType === "upload" ? "upload" : "youtube",
    videoUrl: String(editVideo.videoUrl ?? ""),
    order:
      editVideo.order === undefined || editVideo.order === null
        ? ""
        : String(editVideo.order),
    description: descriptionHtmlToPlain(editVideo.description),
    isPublished: normalizeIsPublished(editVideo.isPublished),
  };
}
async function uploadToStorage(file, storagePath) {
  const storage = getStorage();
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const downloadUrl = await getDownloadURL(storageRef);
  return downloadUrl;
}

async function deleteFromStorage(storagePath) {
  try {
    const storage = getStorage();
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (err) {
    console.warn("[deleteFromStorage] could not delete:", storagePath, err?.code);
  }
}

export default function CreateMegnetVideoModal({ onClose, editVideo = null }) {
  const [form, setForm] = useState(INITIAL);
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(editVideo?.id);

  useEffect(() => {
    if (editVideo?.id) {
      setForm(videoRecordToForm(editVideo));
    } else {
      setForm(INITIAL);
    }
    setVideoFile(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
  }, [editVideo?.id]);

  useEffect(() => {
    if (!thumbnailFile) {
      setThumbnailPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbnailPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);

  const reset = () => {
    setForm(INITIAL);
    setVideoFile(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   const title = form.title.trim();
  //   const videoUrl = form.videoUrl.trim();
  //   if (!title) {
  //     toast.error("Please enter a title.");
  //     return;
  //   }
  //   if (!videoUrl) {
  //     toast.error("Please enter a YouTube video URL.");
  //     return;
  //   }
  //   if (!looksLikeYouTubeUrl(videoUrl)) {
  //     toast.error("Enter a valid YouTube URL.");
  //     return;
  //   }

  //   const orderNum =
  //     form.order === "" || form.order === null
  //       ? 0
  //       : Math.max(0, Number(form.order));

  //   setSaving(true);
  //   try {
  //     if (isEdit) {
  //       await updateDoc(doc(db, "MegnetsVideos", editVideo.id), {
  //         title,
  //         description: toDescriptionHtml(form.description),
  //         order: orderNum,
  //         videoUrl,
  //         isPublished: form.isPublished,
  //       });
  //       toast.success("Video updated.");
  //     } else {
  //       await addDoc(collection(db, "MegnetsVideos"), {
  //         title,
  //         description: toDescriptionHtml(form.description),
  //         order: orderNum,
  //         videoUrl,
  //         thumbnailUrl: null,
  //         isPublished: form.isPublished,
  //         createdAt: serverTimestamp(),
  //       });
  //       toast.success("Video saved.");
  //     }
  //     handleClose();
  //   } catch (err) {
  //     console.error("[CreateMegnetVideo]", err);
  //     toast.error(err?.message || "Could not save video.");
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = form.title.trim();

    if (!title) {
      toast.error("Please enter a title.");
      return;
    }

    if (form.uploadType === "youtube") {
      const videoUrl = form.videoUrl.trim();
      if (!videoUrl) {
        toast.error("Please enter a YouTube video URL.");
        return;
      }
      if (!looksLikeYouTubeUrl(videoUrl)) {
        toast.error("Enter a valid YouTube URL.");
        return;
      }
    } else {
      if (!isEdit && !videoFile) {
        toast.error("Please select a video file to upload.");
        return;
      }
    }

    const orderNum =
      form.order === "" || form.order === null
        ? 0
        : Math.max(0, Number(form.order));

    setSaving(true);
    try {
      if (isEdit) {
        // --- EDIT CASE (Step 4 - coming next) ---
        const docId = editVideo.id;
        let contentUrl = editVideo.contentUrl ?? editVideo.videoUrl ?? null;
        let thumbnailUrl = editVideo.thumbnailUrl ?? null;

        if(form.uploadType === "youtube") {
          if (editVideo.uploadType === "upload" && editVideo.contentUrl) {
            await deleteFromStorage(`videos/${docId}/video/video.mp4`);
          }
          contentUrl = form.videoUrl.trim();
        }else{
          if(videoFile){
            if (editVideo.uploadType === "upload" && editVideo.contentUrl) {
              await deleteFromStorage(`videos/${docId}/video/video.mp4`);
            }
            toast.loading("Uploading video...", { id: "upload" });
            contentUrl = await uploadToStorage(
              videoFile,
              `videos/${docId}/video/video.mp4`
            );
            toast.dismiss("upload");
          }
        }
        if(thumbnailFile){
          if(editVideo.thumbnailUrl){
            await deleteFromStorage(`videos/${docId}/thumbnail/thumbnail.jpg`);
          }
          toast.loading("Uploading thumbnail...", { id: "thumb" });
        thumbnailUrl = await uploadToStorage(
          thumbnailFile,
          `videos/${docId}/thumbnail/thumbnail.jpg`
        );
        toast.dismiss("thumb");
        }
      
        await updateDoc(doc(db, "MegnetsVideos", editVideo.id), {
          title,
          description: toDescriptionHtml(form.description),
          order: orderNum,
          uploadType: form.uploadType,
          // videoUrl: form.uploadType === "youtube" ? form.videoUrl.trim() : editVideo.videoUrl,
          contentUrl,
          videoUrl: contentUrl,
          thumbnailUrl,
          isPublished: form.isPublished,
        });
        toast.success("Video updated.");
        handleClose();
      } else {
        // --- CREATE CASE ---

        // Step 3a → Create Firestore doc first to get docId
        const docRef = await addDoc(collection(db, "MegnetsVideos"), {
          title,
          description: toDescriptionHtml(form.description),
          order: orderNum,
          uploadType: form.uploadType,
          contentUrl: form.uploadType === "youtube" ? form.videoUrl.trim() : null,
          videoUrl: form.uploadType === "youtube" ? form.videoUrl.trim() : null,
          thumbnailUrl: null,
          isPublished: form.isPublished,
          createdAt: serverTimestamp(),
        });

        const docId = docRef.id;
        let contentUrl = form.uploadType === "youtube" ? form.videoUrl.trim() : null;
        let thumbnailUrl = null;

        // Step 3b → Upload video if uploadType is "upload"
        if (form.uploadType === "upload" && videoFile) {
          toast.loading("Uploading video...", { id: "upload" });
          contentUrl = await uploadToStorage(
            videoFile,
            `videos/${docId}/video/video.mp4`
          );
          toast.dismiss("upload");
        }

        // Step 3c → Upload thumbnail if selected
        if (thumbnailFile) {
          toast.loading("Uploading thumbnail...", { id: "thumb" });
          thumbnailUrl = await uploadToStorage(
            thumbnailFile,
            `videos/${docId}/thumbnail/thumbnail.jpg`
          );
          toast.dismiss("thumb");
        }

        // Step 3d → Update Firestore doc with final URLs
        await updateDoc(doc(db, "MegnetsVideos", docId), {
          contentUrl,
          videoUrl: contentUrl,
          thumbnailUrl,
        });

        toast.success("Video saved.");
        handleClose();
      }
    } catch (err) {
      console.error("[CreateMegnetVideo]", err);
      toast.error(err?.message || "Could not save video.");
    } finally {
      setSaving(false);
    }
  };


  const inputClass =
    "w-full px-3 py-2.5 rounded-xl bg-slate-900/80 border border-slate-600/80 text-slate-100 text-sm text-left placeholder-slate-500 focus:outline-none focus:border-blue-500/70 transition-colors";
  const labelClass =
    "block text-sm font-semibold text-slate-200 mb-1.5 text-left";
  const fileInputClass = `${inputClass} file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-slate-600 file:text-white hover:file:bg-slate-500`;

  return (
    <Modal
      onClose={handleClose}
      contentClassName="w-full max-w-2xl px-4 max-w-[600px] sm:px-6"
    >
      <div className="megnet-modal-scroll mt-8 rounded-2xl border border-slate-700/60 bg-[#12151c] p-6 md:p-8 shadow-2xl max-h-[85vh] overflow-y-auto text-left">
        <h2 className="text-xl font-bold text-white tracking-tight mb-6 text-left">
          {isEdit ? "Edit Video" : "Create Video"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-5 text-left">
          <div>
            <label htmlFor="cmv-title" className={labelClass}>
              Title
            </label>
            <input
              id="cmv-title"
              type="text"
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
              className={inputClass}
              placeholder="Video title"
              autoComplete="off"
            />
          </div>

          {/* <div>
            <label htmlFor="cmv-video-url" className={labelClass}>
              YouTube URL
            </label>
            <input
              id="cmv-video-url"
              type="url"
              inputMode="url"
              value={form.videoUrl}
              onChange={(e) =>
                setForm((f) => ({ ...f, videoUrl: e.target.value }))
              }
              className={inputClass}
              placeholder="https://www.youtube.com/watch?v=…"
              autoComplete="off"
            />
          </div> */}

          {/* Upload Type Toggle */}
          <div>
            <span className={labelClass}>Upload Type</span>
            <div className="flex gap-2" role="group" aria-label="Upload type">
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({ ...f, uploadType: "youtube", videoUrl: "" }))
                }
                className={`flex-1 rounded-xl py-3 text-sm font-bold transition-colors ${form.uploadType === "youtube"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "border border-slate-600 bg-slate-900/40 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                  }`}
              >
                YouTube URL
              </button>
              <button
                type="button"
                onClick={() => {
                  setForm((f) => ({ ...f, uploadType: "upload", videoUrl: "" }));
                  setVideoFile(null);
                }}
                className={`flex-1 rounded-xl py-3 text-sm font-bold transition-colors ${form.uploadType === "upload"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "border border-slate-600 bg-slate-900/40 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                  }`}
              >
                Upload Video
              </button>
            </div>
          </div>

          {/* Conditional Field based on Upload Type */}
          {form.uploadType === "youtube" ? (
            <div>
              <label htmlFor="cmv-video-url" className={labelClass}>
                YouTube URL
              </label>
              <input
                id="cmv-video-url"
                type="url"
                inputMode="url"
                value={form.videoUrl}
                onChange={(e) =>
                  setForm((f) => ({ ...f, videoUrl: e.target.value }))
                }
                className={inputClass}
                placeholder="https://www.youtube.com/watch?v=…"
                autoComplete="off"
              />
            </div>
          ) : (
            <div>
              <label htmlFor="cmv-video-file" className={labelClass}>
                Video File
              </label>
              <input
                id="cmv-video-file"
                type="file"
                accept="video/*"
                onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)}
                className={fileInputClass}
              />
              {videoFile && (
                <p className="mt-2 text-xs text-slate-400">
                  Selected: {videoFile.name}
                </p>
              )}
              {/* Show existing video info in edit mode */}
              {isEdit && editVideo?.uploadType === "upload" && editVideo?.contentUrl && !videoFile && (
                <p className="mt-2 text-xs text-emerald-400">
                  ✓ Video already uploaded. Select a new file to replace it.
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="cmv-thumb" className={labelClass}>
              Thumbnail
            </label>
            <input
              id="cmv-thumb"
              name="thumbnailFile"
              type="file"
              accept="image/*"
              onChange={(e) => setThumbnailFile(e.target.files?.[0] ?? null)}
              className={fileInputClass}
            />
            {(thumbnailPreview ||
              (isEdit && editVideo?.thumbnailUrl && !thumbnailFile)) ? (
              <div className="mt-3 w-40 aspect-video rounded-lg overflow-hidden border border-slate-600/80">
                <img
                  src={thumbnailPreview || editVideo.thumbnailUrl}
                  alt="Thumbnail preview"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : null}
          </div>

          <div>
            <label htmlFor="cmv-order" className={labelClass}>
              Order
            </label>
            <input
              id="cmv-order"
              type="number"
              min={0}
              step={1}
              value={form.order}
              onChange={(e) =>
                setForm((f) => ({ ...f, order: e.target.value }))
              }
              className={inputClass}
              placeholder="0"
            />
          </div>

          <div>
            <label htmlFor="cmv-desc" className={labelClass}>
              Description
            </label>
            <textarea
              id="cmv-desc"
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              rows={5}
              className={`${inputClass} resize-y min-h-[120px]`}
              placeholder="Description"
            />
          </div>

          <div>
            <span className={labelClass}>Status</span>
            <div
              className="flex gap-2"
              role="group"
              aria-label="Publication status"
            >
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({ ...f, isPublished: "success" }))
                }
                className={`flex-1 rounded-xl py-3 text-sm font-bold transition-colors ${form.isPublished === "success"
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "border border-slate-600 bg-slate-900/40 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                  }`}
              >
                Success
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({ ...f, isPublished: "draft" }))
                }
                className={`flex-1 rounded-xl py-3 text-sm font-bold transition-colors ${form.isPublished === "draft"
                    ? "border border-slate-500 bg-slate-800 text-white"
                    : "border border-slate-600 bg-slate-900/40 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                  }`}
              >
                Draft
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="text-sm font-semibold text-slate-300 hover:text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold px-5 py-2.5 shadow-lg transition-colors"
            >
              {saving ? "Saving…" : isEdit ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
